import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, Plus, X, Check, AlertCircle, Search, Grid, List, 
  GripVertical, ArrowUpDown, Eye, EyeOff, Zap, Target,
  TrendingUp, Filter, Download, RefreshCw, Sparkles
} from 'lucide-react';

function FeaturedProductsManagement({ products, featuredProducts, updateFeaturedProducts, fetchFeaturedProducts, isMobile }) {
  const [availableProducts, setAvailableProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    const available = products.filter(product => 
      product.isActive && 
      !featuredProducts.some(fp => fp.id === product.id)
    );
    setAvailableProducts(available);
  }, [products, featuredProducts]);

  const filteredAvailable = availableProducts.filter(product => {
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = !filters.category || product.category === filters.category;
    const matchesMinPrice = !filters.minPrice || product.price >= parseFloat(filters.minPrice);
    const matchesMaxPrice = !filters.maxPrice || product.price <= parseFloat(filters.maxPrice);

    return matchesSearch && matchesCategory && matchesMinPrice && matchesMaxPrice;
  });

  const handleAddToFeatured = (product) => {
    const newFeaturedProduct = {
      id: product.id,
      name: product.name,
      image: product.image,
      description: product.description.substring(0, 100) + '...',
      price: product.price,
      category: product.category,
      rating: 4.8,
      tags: ['New', 'Popular']
    };
    
    const updatedFeatured = [...featuredProducts, newFeaturedProduct];
    updateFeaturedProducts(updatedFeatured);
  };

  const handleRemoveFromFeatured = (productId) => {
    const updatedFeatured = featuredProducts.filter(fp => fp.id !== productId);
    updateFeaturedProducts(updatedFeatured);
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleBulkAdd = () => {
    setIsSaving(true);
    selectedProducts.forEach(productId => {
      const product = availableProducts.find(p => p.id === productId);
      if (product) {
        handleAddToFeatured(product);
      }
    });
    setTimeout(() => {
      setSelectedProducts([]);
      setIsSaving(false);
    }, 1000);
  };

  const handleReorder = (fromIndex, toIndex) => {
    const updatedFeatured = [...featuredProducts];
    const [movedProduct] = updatedFeatured.splice(fromIndex, 1);
    updatedFeatured.splice(toIndex, 0, movedProduct);
    updateFeaturedProducts(updatedFeatured);
  };

  const handleExportFeatured = () => {
    // Export featured products logic
    alert('Featured products exported successfully!');
  };

  const categories = ['Premium Cuts', 'BBQ Specials', 'Traditional Cuts', 'Wholesale'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            Featured Products
          </h2>
          <p className="text-gray-400">Showcase your best products on the homepage</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {selectedProducts.length > 0 && (
            <motion.button
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              onClick={handleBulkAdd}
              disabled={isSaving}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center space-x-2"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Add {selectedProducts.length} Selected</span>
                </>
              )}
            </motion.button>
          )}
          
          <div className="flex bg-gray-800 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Featured Products Section */}
      <div className="relative">
        {/* Glow Effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-3xl blur-xl"></div>
        
        {/* Container */}
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-amber-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Featured Spotlight</h3>
                <p className="text-gray-400">Drag to reorder (first 6 appear on homepage)</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 rounded-full text-sm">
                {featuredProducts.length} featured
              </span>
              <button
                onClick={handleExportFeatured}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
          </div>

          {featuredProducts.length > 0 ? (
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
              {featuredProducts.map((product, index) => (
                <FeaturedProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  onRemove={handleRemoveFromFeatured}
                  onReorder={handleReorder}
                  totalItems={featuredProducts.length}
                  isMobile={isMobile}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-amber-500" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">No featured products yet</h4>
              <p className="text-gray-400 mb-6">Add some products to showcase them on the homepage!</p>
              <button
                onClick={() => document.getElementById('available-products')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
              >
                Browse Products
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Available Products Section */}
      <div id="available-products" className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Available Products</h3>
            <p className="text-gray-400">Select products to feature on homepage</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 w-full md:w-64"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-800/30 rounded-lg mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Min Price</label>
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="$0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Max Price</label>
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="$100"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {filteredAvailable.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
            {filteredAvailable.map((product) => (
              <AvailableProductCard
                key={product.id}
                product={product}
                viewMode={viewMode}
                isSelected={selectedProducts.includes(product.id)}
                onSelect={() => handleSelectProduct(product.id)}
                onAdd={() => handleAddToFeatured(product)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-emerald-500" />
            </div>
            <h4 className="text-lg font-bold text-white mb-2">
              {availableProducts.length === 0 ? 'All products are featured!' : 'No matching products found'}
            </h4>
            <p className="text-gray-400">
              {availableProducts.length === 0 
                ? 'All active products are already featured.' 
                : 'Try a different search term or filter.'}
            </p>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-400">Featured Rate</div>
              <div className="text-2xl font-bold text-white">
                {((featuredProducts.length / products.length) * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-400">Total Products</div>
              <div className="text-2xl font-bold text-white">{products.length}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-400">Available for Feature</div>
              <div className="text-2xl font-bold text-white">{availableProducts.length}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedProductCard({ product, index, onRemove, onReorder, totalItems, isMobile }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', index.toString());
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
    const toIndex = index;
    onReorder(fromIndex, toIndex);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      className={`bg-gradient-to-br from-gray-900 to-black rounded-2xl border ${isDragging ? 'border-dashed border-amber-500' : 'border-gray-800'} overflow-hidden`}
    >
      <div className="p-6">
        {/* Card Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
              <span className="text-white font-bold">{index + 1}</span>
            </div>
            {index < 6 && (
              <span className="px-2 py-1 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-400 text-xs rounded-full">
                On Homepage
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onRemove(product.id)}
              className="p-1 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-red-400" />
            </button>
            <div className="p-1 text-gray-500 cursor-grab">
              <GripVertical className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="flex items-center gap-4">
          <img
            src={product.image}
            alt={product.name}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div className="flex-1">
            <h4 className="font-bold text-white truncate">{product.name}</h4>
            <p className="text-sm text-gray-400 line-clamp-2">{product.description}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-amber-400 font-bold">${product.price}</span>
              <span className="text-xs px-2 py-1 bg-gray-800 rounded">{product.category}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <Star className="w-3 h-3 text-amber-500" />
              <span>{product.rating || 4.8}</span>
            </div>
            <span>Position: {index + 1} of {totalItems}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AvailableProductCard({ product, viewMode, isSelected, onSelect, onAdd }) {
  if (viewMode === 'list') {
    return (
      <motion.div
        whileHover={{ x: 4 }}
        className="flex items-center justify-between p-4 bg-gray-800/30 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors"
      >
        <div className="flex items-center gap-4">
          <button
            onClick={onSelect}
            className={`w-6 h-6 rounded border flex items-center justify-center ${isSelected ? 'bg-gradient-to-r from-emerald-500 to-green-600 border-emerald-600' : 'border-gray-600'}`}
          >
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </button>
          
          <img
            src={product.image}
            alt={product.name}
            className="w-12 h-12 rounded-lg object-cover"
          />
          
          <div>
            <h4 className="font-bold text-white">{product.name}</h4>
            <p className="text-sm text-gray-400">{product.category}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-lg font-bold text-amber-400">${product.price}</div>
            <div className="text-sm text-gray-500">{product.weight}</div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAdd}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg transition-all"
          >
            Feature
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden group"
    >
      <div className="relative">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-48 object-cover"
        />
        
        <button
          onClick={onSelect}
          className={`absolute top-3 left-3 w-6 h-6 rounded border flex items-center justify-center ${isSelected ? 'bg-gradient-to-r from-emerald-500 to-green-600 border-emerald-600' : 'bg-gray-900 border-gray-700'}`}
        >
          {isSelected && <Check className="w-3 h-3 text-white" />}
        </button>
        
        <span className="absolute top-3 right-3 px-2 py-1 bg-gray-900/80 text-white text-xs rounded">
          {product.category}
        </span>
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
      </div>
      
      <div className="p-6">
        <h4 className="font-bold text-white mb-2">{product.name}</h4>
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{product.description}</p>
        
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-bold text-amber-400">${product.price}</div>
            <div className="text-sm text-gray-500">{product.weight}</div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAdd}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Star className="w-4 h-4" />
            <span>Feature</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

export default FeaturedProductsManagement;